import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { XMarkIcon, ShoppingCartIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useCart } from '../../contexts/CartContext'
import { CartItemCard } from './CartItemCard'
import { CartSummary } from './CartSummary'
import { InvoiceBuilder } from '../invoices/InvoiceBuilder'
import Button from '../ui/Button'

interface CartSidebarProps {
  isOpen: boolean
  onClose: () => void
}

export function CartSidebar({ isOpen, onClose }: CartSidebarProps) {
  const { items, itemCount, clearCart, cart } = useCart()
  const sidebarRef = useRef<HTMLDivElement>(null)
  const [showInvoiceBuilder, setShowInvoiceBuilder] = useState(false)
  
  console.log('CartSidebar rendered, isOpen:', isOpen)

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  // Handle click outside
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!isOpen) return null

  return createPortal(
    <div
      className="fixed inset-0 z-50 overflow-hidden"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* Sidebar Panel */}
      <div className="fixed inset-y-0 right-0 flex max-w-full pl-10">
        <div
          ref={sidebarRef}
          className={`pointer-events-auto w-screen max-w-md transform transition-transform duration-300 ease-in-out ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex h-full flex-col bg-[#1a1a1a] shadow-xl">
            {/* Header */}
            <div className="border-b border-gray-800 px-4 py-6 sm:px-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  <ShoppingCartIcon className="h-6 w-6 text-gold mr-2" />
                  <h2 className="text-lg font-medium text-white">
                    Shopping Cart
                  </h2>
                </div>
                <div className="ml-3 flex h-7 items-center">
                  <button
                    type="button"
                    className="relative -m-2 p-2 text-gray-400 hover:text-gray-300 transition-colors"
                    onClick={onClose}
                  >
                    <span className="absolute -inset-0.5" />
                    <span className="sr-only">Close panel</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
              </div>
              <div className="mt-2">
                <p className="text-sm text-gray-400">
                  {itemCount} {itemCount === 1 ? 'item' : 'items'} in cart
                </p>
              </div>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <ShoppingCartIcon className="h-12 w-12 text-gray-600 mb-4" />
                  <p className="text-gray-400">Your cart is empty</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Add bookings to get started
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {items.map((item) => (
                    <CartItemCard key={item.id} item={item} />
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-800 px-4 py-6 sm:px-6">
              <CartSummary className="mb-4" showBreakdown={true} />
              
              {items.length > 0 && (
                <>
                  <Button
                    variant="primary"
                    className="w-full mb-3"
                    onClick={() => setShowInvoiceBuilder(true)}
                  >
                    Create Invoice
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to clear the cart?')) {
                        clearCart()
                      }
                    }}
                  >
                    Clear Cart
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Invoice Builder Modal */}
      <InvoiceBuilder
        isOpen={showInvoiceBuilder}
        onClose={() => setShowInvoiceBuilder(false)}
        clientId={cart?.client_id || undefined}
      />
    </div>,
    document.body
  )
}